import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fsGet, fsSet, clientesToFields, docToClientes, mesAtual, mrrEfetivo,
  mesclarClientes, dataISO, normStatus, normPeriodo, valorToNumber,
  CATALOGO_PLATAFORMA,
  separarValores, juntarValores, valoresToFields, docToValores,
} from './Clientes';

// ─── De-para: nome do componente da Avec → nome do catálogo do painel ─────────
const PLANO_POR_PROF = {
  2: 'Até 2 Prof.', 5: 'Até 5 Prof.', 10: 'Até 10 Prof.', 20: 'Até 20 Prof.',
  30: 'Até 30 Prof.', 40: 'Até 40 Prof.', 50: 'Até 50 Prof.',
};
const FAIXA_IA_POR_PLANO = Object.fromEntries(CATALOGO_PLATAFORMA.map(p => [p.nome, p.faixaIA]));

const MODULOS = [
  [/nfs-?e|nf de servico|nota fiscal de servico/, 'NF de Serviço (NFS-e)'],
  [/nfc-?e|nf de produto|nota fiscal de produto/, 'NF de Produto'],
  [/franquia/,                                    'Módulo Franquia'],
  [/1\.?000\s*sms/,                               '1.000 SMS Avec'],
  [/500\s*sms/,                                   '500 SMS Avec'],
  [/white\s?label/,                               'White Label (manutenção)'],
];
const IA_COMPONENTES = [[/confirmacao/, 'Confirmação'], [/agendamento/, 'Agendamento'], [/marketing/, 'Marketing']];

const semAcento = (v) => String(v == null ? '' : v).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const chaveNome = (n) => semAcento(n).toLowerCase().replace(/[^a-z0-9]/g, '');
const soDigitos = (v) => String(v == null ? '' : v).replace(/\D/g, '');

function identificaPlano(texto) {
  const t = semAcento(texto).toUpperCase();
  if (t.includes('AVECGO')) return 'AVECGO / Até 2';
  if (t.includes('ILIMITAD')) return 'Ilimitado';
  const m = t.match(/ATE\s*(\d+)\s*PROFISSIONA/);
  return m ? (PLANO_POR_PROF[parseInt(m[1], 10)] || null) : null;
}

function classificaComponente(nome, periodicidade) {
  const t = semAcento(nome).toLowerCase().trim();
  if (String(periodicidade || '').trim().toUpperCase() === 'ONE-SHOT') return ['oneshot', String(nome).trim()];
  if (t.includes('avecia')) {
    const ia = IA_COMPONENTES.find(([rx]) => rx.test(t));
    if (ia) return ['ia', ia[1]];
  }
  const mod = MODULOS.find(([rx]) => rx.test(t));
  if (mod) return ['modulo', mod[1]];
  const plano = identificaPlano(nome);
  if (plano) return ['plano', plano];
  if (t === 'plataforma avec' || t === 'plataforma avec - assinatura') return ['ignorar', null];
  return ['desconhecido', String(nome).trim()];
}

// ─── Leitura das planilhas ────────────────────────────────────────────────────
async function lerPlanilha(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const linhas = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
  if (!linhas.length) return [];
  const cab = linhas[0].map(h => semAcento(h).trim().toLowerCase().replace(/\s+/g, '_'));
  return linhas.slice(1)
    .filter(cols => cols.some(c => String(c).trim() !== ''))
    .map(cols => {
      const o = {};
      cab.forEach((h, i) => { o[h] = cols[i] == null ? '' : cols[i]; });
      return o;
    });
}

const somaCampos = (o, nomes) => nomes.reduce((s, n) => {
  const v = o[n];
  if (v === undefined || v === null || String(v).trim() === '') return s;
  const n2 = valorToNumber(v);
  return s + (typeof n2 === 'number' && isFinite(n2) ? n2 : 0);
}, 0);

const col = (o, ...nomes) => {
  for (const n of nomes) {
    const v = o[n];
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return '';
};

// ─── Mesclagem das duas planilhas ─────────────────────────────────────────────
export function mesclarCarteira(carteira, contratos) {
  const porDoc = {}, porId = {}, porNome = {};
  contratos.forEach(r => {
    const d = soDigitos(col(r, 'documento', 'cnpj'));
    const i = String(col(r, 'cliente_id', 'id')).trim();
    const n = chaveNome(col(r, 'nome'));
    if (d) (porDoc[d] = porDoc[d] || []).push(r);
    if (i) (porId[i] = porId[i] || []).push(r);
    if (n) (porNome[n] = porNome[n] || []).push(r);
  });

  const vistos = new Set();
  const clientes = [], conferencia = [];

  carteira.forEach((c, idx) => {
    const doc = soDigitos(col(c, 'documento', 'cnpj'));
    const cid = String(col(c, 'cliente_id', 'id')).trim();
    const nome = String(col(c, 'nome')).trim();

    let grupo = [], via = 'sem contrato';
    if (doc && porDoc[doc]) { grupo = porDoc[doc]; via = 'Documento'; }
    else if (cid && porId[cid]) { grupo = porId[cid]; via = 'Cliente ID'; }
    else if (porNome[chaveNome(nome)]) { grupo = porNome[chaveNome(nome)]; via = 'Nome'; }
    vistos.add(chaveNome(nome));

    const produtos = [], iaMods = [], oneshots = [], desconhecidos = [];
    grupo.forEach(r => {
      const st = String(col(r, 'status_componente')).trim().toUpperCase();
      if (st && st !== 'ATIVO') return;
      const [tipo, val] = classificaComponente(col(r, 'nome_componente'), col(r, 'periodicidade_componente'));
      if (tipo === 'plano' || tipo === 'modulo') { if (!produtos.includes(val)) produtos.push(val); }
      else if (tipo === 'ia') { if (!iaMods.includes(val)) iaMods.push(val); }
      else if (tipo === 'oneshot') oneshots.push(val);
      else if (tipo === 'desconhecido') desconhecidos.push(val);
    });

    // se nenhum componente trouxe o plano, tenta pelo nome do contrato
    if (grupo.length && !produtos.some(p => FAIXA_IA_POR_PLANO[p])) {
      const plano = identificaPlano(col(grupo[0], 'nome_contrato'));
      if (plano) produtos.unshift(plano);
    }

    const status = normStatus(col(c, 'status_contrato', 'status'), 'Ativo');
    const dataChurn = dataISO(col(c, 'data_churn'));
    const temMaq = oneshots.some(x => /maquininha|pos\b|zoop/i.test(x));
    const obs = [
      (status !== 'Ativo' && dataChurn) ? `Churn em ${dataChurn.split('-').reverse().join('/')}` : '',
      oneshots.length ? `One-shot: ${oneshots.join(', ')}` : '',
      temMaq ? 'Maquininha contratada' : '',
    ].filter(Boolean).join(' | ');

    const planoAtual = produtos.find(p => FAIXA_IA_POR_PLANO[p]);

    clientes.push({
      id: Date.now() + idx,
      clienteId: cid,
      documento: String(col(c, 'documento', 'cnpj')),
      nome,
      email: '', telefone: '', aniversario: '', contato: '',
      observacao: obs,
      sistemaCobranca: String(col(c, 'sistema_cobranca') || 'Avec'),
      statusContrato: status,
      periodicidadeContrato: normPeriodo(grupo.length ? col(grupo[0], 'periodicidade_contrato') : '', 'Mensal'),
      ativacaoContrato: dataISO(col(c, 'ativacao_contrato')),
      expiracaoContrato: dataISO(col(c, 'expiracao_contrato')),
      produtos,
      iaFaixa: FAIXA_IA_POR_PLANO[planoAtual] || '',
      // os componentes AVECIA da 2a planilha viram os modulos de IA do cadastro
      iaModulos: iaMods.map(m => ({ 'Confirmação': 'ci', 'Agendamento': 'cs', 'Marketing': 'cm' }[m])).filter(Boolean),
      // usa o mesmo parser do painel: 286.9 e "R$ 1.234,56" caem certo
      mrrCustom: valorToNumber(col(c, 'mrr_(r$)', 'mrr', 'valor')),
      // TPV/Net Take dizem se a maquininha esta rodando: sem TPV, nao usa.
      tpvMes:   somaCampos(c, ['tpv_lote_zoop_(r$)', 'tpv_lote_zoop', 'tpv_lote_hyperlocal',
                               'tpv_transacional_(r$)', 'tpv_transacional', 'tpv']),
      netTakeMes: somaCampos(c, ['net_take_lote_zoop_(r$)', 'net_take_lote_zoop',
                                 'net_take_lote_hyperlocal', 'net_take']),
    });

    conferencia.push({ nome, via, componentes: grupo.length, produtos, iaMods, oneshots, desconhecidos, status });
  });

  const orfaos = Object.keys(porNome)
    .filter(k => !vistos.has(k))
    .map(k => String(col(porNome[k][0], 'nome')));

  return { clientes, conferencia, orfaos };
}

// ─── Tela ─────────────────────────────────────────────────────────────────────
export default function Carteira() {
  // contaUid: a carteira é sempre a do DONO da conta, mesmo quando quem
  // está mexendo é um vendedor convidado.
  const { user, contaUid } = useAuth();
  const navigate = useNavigate();
  const [fCarteira, setFCarteira] = useState(null);
  const [fContratos, setFContratos] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState('');
  const [lendo, setLendo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const analisar = async () => {
    if (!fCarteira || !fContratos) { setErro('Selecione as duas planilhas.'); return; }
    setLendo(true); setErro(''); setPrevia(null); setResultado(null);
    try {
      const [carteira, contratos] = await Promise.all([lerPlanilha(fCarteira), lerPlanilha(fContratos)]);
      if (!carteira.length) throw new Error('A planilha da carteira veio vazia.');
      if (!carteira[0].nome && !carteira[0].cliente_id) {
        throw new Error('Não encontrei as colunas da carteira. A primeira linha precisa ser o cabeçalho (Cliente ID, Documento, Nome...).');
      }
      const r = mesclarCarteira(carteira, contratos);
      if (!r.clientes.length) throw new Error('Nenhum cliente encontrado na carteira.');
      setPrevia(r);
    } catch (e) {
      setErro(e.message || String(e));
    } finally { setLendo(false); }
  };

  const aplicar = async () => {
    if (!previa || !user || !contaUid) return;
    setSalvando(true); setErro('');
    try {
      const token = await user.getIdToken();
      const doc = await fsGet(`users/${contaUid}/clientes/dados`, token);
      // a base guardada não tem mais valor dentro: junta antes de mesclar,
      // senão o mesclarClientes acha que todo mundo perdeu o negociado.
      const dv = await fsGet(`users/${contaUid}/financeiro/valores`, token);
      const valoresAtuais = docToValores(dv);
      const base = juntarValores(doc ? docToClientes(doc) : [], valoresAtuais);

      const { lista, resumo } = mesclarClientes(base, previa.clientes);

      // e separa de novo na hora de gravar
      const { limpos, valores } = separarValores(lista);
      await fsSet(`users/${contaUid}/clientes/dados`, clientesToFields(limpos), token);
      await fsSet(`users/${contaUid}/financeiro/valores`, valoresToFields(valores), token);

      const mes = mesAtual();
      await fsSet(`users/${contaUid}/snapshots/${mes}`, {
        mes:      { stringValue: mes },
        mrr:      { doubleValue: lista.reduce((s, c) => s + mrrEfetivo(c), 0) },
        total:    { integerValue: lista.length },
        ativos:   { integerValue: lista.filter(c => c.statusContrato === 'Ativo').length },
        clientes: { stringValue: JSON.stringify(lista) },
        savedAt:  { stringValue: new Date().toISOString() },
      }, token);
      setResultado({ ...resumo, total: lista.length });
    } catch (e) {
      setErro('Não consegui salvar: ' + (e.message || e));
    } finally { setSalvando(false); }
  };

  const precisaAtencao = previa
    ? previa.conferencia.filter(c => c.desconhecidos.length || c.via === 'sem contrato' || c.via === 'Nome')
    : [];
  const comIA = previa ? previa.conferencia.filter(c => c.iaMods.length) : [];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>
          Atualizar carteira
        </h1>
        <p style={{ color: '#52526e', fontSize: '13px' }}>
          Junta as duas planilhas da Avec — a da carteira (MRR) e a de contratos e componentes (produtos) — e atualiza sua base de clientes.
        </p>
      </div>

      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <Upload id="up-carteira" titulo="1. Carteira" dica="Tem o MRR e o status de cada cliente"
            file={fCarteira} onPick={f => { setFCarteira(f); setPrevia(null); setResultado(null); }} />
          <Upload id="up-contratos" titulo="2. Contratos e componentes" dica="Tem os produtos contratados"
            file={fContratos} onPick={f => { setFContratos(f); setPrevia(null); setResultado(null); }} />
        </div>

        {erro && (
          <div style={{ ...aviso, background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.2)', color: '#dc2626' }}>
            ⚠ {erro}
          </div>
        )}

        {!previa && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={analisar} disabled={!fCarteira || !fContratos || lendo}
              style={btn(!!fCarteira && !!fContratos && !lendo)}>
              {lendo ? 'Lendo...' : 'Analisar →'}
            </button>
          </div>
        )}

        {previa && !resultado && (
          <>
            <div style={{ ...aviso, background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.2)', color: '#059669' }}>
              {previa.clientes.length} clientes lidos ·{' '}
              {previa.clientes.filter(c => c.statusContrato === 'Ativo').length} ativos ·{' '}
              MRR R$ {previa.clientes.reduce((s, c) => s + (Number(c.mrrCustom) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>

            <div style={{ maxHeight: '280px', overflow: 'auto', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', marginBottom: '14px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8f8fc', position: 'sticky', top: 0 }}>
                    {['Cliente', 'Status', 'Produtos', 'MRR'].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previa.clientes.map((c, i) => (
                    <tr key={i} style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                      <td style={td}>{c.nome}</td>
                      <td style={{ ...td, color: c.statusContrato === 'Ativo' ? '#059669' : '#9898b8' }}>{c.statusContrato}</td>
                      <td style={{ ...td, color: '#52526e' }}>{c.produtos.join(' · ') || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>
                        {c.mrrCustom === '' ? '—' : Number(c.mrrCustom).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {comIA.length > 0 && (
              <div style={{ ...aviso, background: 'rgba(108,71,255,0.06)', borderColor: 'rgba(108,71,255,0.2)', color: '#6c47ff' }}>
                <strong>{comIA.length} cliente(s) com módulos de IA</strong> — já vêm marcados no cadastro:
                <div style={{ marginTop: '6px', fontSize: '12px', lineHeight: '1.7' }}>
                  {comIA.slice(0, 12).map(c => <div key={c.nome}>· {c.nome}: {c.iaMods.join(', ')}</div>)}
                  {comIA.length > 12 && <div>· e mais {comIA.length - 12}...</div>}
                </div>
              </div>
            )}

            {precisaAtencao.length > 0 && (
              <div style={{ ...aviso, background: 'rgba(245,158,11,0.07)', borderColor: 'rgba(245,158,11,0.25)', color: '#b45309' }}>
                <strong>{precisaAtencao.length} precisam de conferência</strong>
                <div style={{ marginTop: '6px', fontSize: '12px', lineHeight: '1.7' }}>
                  {precisaAtencao.slice(0, 12).map(c => (
                    <div key={c.nome}>
                      · {c.nome}
                      {c.via === 'sem contrato' && ' — sem contrato na 2ª planilha (normal para quem já saiu)'}
                      {c.via === 'Nome' && ' — casou pelo nome, confira se é o mesmo salão'}
                      {c.desconhecidos.length > 0 && ` — fora do catálogo: ${c.desconhecidos.join(', ')}`}
                    </div>
                  ))}
                  {precisaAtencao.length > 12 && <div>· e mais {precisaAtencao.length - 12}...</div>}
                </div>
              </div>
            )}

            {previa.orfaos.length > 0 && (
              <div style={{ ...aviso, background: 'rgba(245,158,11,0.07)', borderColor: 'rgba(245,158,11,0.25)', color: '#b45309' }}>
                {previa.orfaos.length} salão(ões) aparecem nos contratos mas não na carteira e ficaram de fora:{' '}
                {previa.orfaos.slice(0, 6).join(', ')}{previa.orfaos.length > 6 ? '...' : ''}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '12px', color: '#9898b8' }}>
                Atualiza contrato, produtos e MRR. WhatsApp, e-mail e aniversário que você digitou continuam como estão.
              </span>
              <button onClick={aplicar} disabled={salvando || !user || !contaUid} style={btn(!salvando && !!user && !!contaUid)}>
                {salvando ? 'Salvando...' : 'Atualizar base →'}
              </button>
            </div>
            {!user && <div style={{ ...aviso, marginTop: '12px', background: 'rgba(108,71,255,0.06)', borderColor: 'rgba(108,71,255,0.2)', color: '#6c47ff' }}>🔒 Entre na sua conta para atualizar a base.</div>}
          </>
        )}

        {resultado && (
          <div style={{ ...aviso, background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.2)', color: '#059669' }}>
            <strong>Base atualizada — {resultado.total} clientes no total.</strong>
            <div style={{ marginTop: '6px', fontSize: '12px', lineHeight: '1.8', color: '#52526e' }}>
              · {resultado.novos} novos<br />
              · {resultado.atualizados} atualizados<br />
              {resultado.viraramChurn.length > 0 && <>· {resultado.viraramChurn.length} saíram de ativo: {resultado.viraramChurn.slice(0, 6).join(', ')}{resultado.viraramChurn.length > 6 ? '...' : ''}<br /></>}
              {resultado.reativados.length > 0 && <>· {resultado.reativados.length} reativados: {resultado.reativados.slice(0, 6).join(', ')}<br /></>}
              {resultado.ausentes.length > 0 && <>· {resultado.ausentes.length} da sua base não vieram nesta planilha (mantidos)<br /></>}
            </div>
            <button onClick={() => navigate('/clientes')} style={{ ...btn(true), marginTop: '10px' }}>Ver clientes →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Upload({ id, titulo, dica, file, onPick }) {
  return (
    <div>
      <label style={lbl}>{titulo}</label>
      <div onClick={() => document.getElementById(id).click()}
        style={{
          border: '2px dashed rgba(108,71,255,0.3)', borderRadius: '12px', padding: '20px',
          textAlign: 'center', cursor: 'pointer', background: file ? 'rgba(108,71,255,0.04)' : '#f8f8fc', marginTop: '6px',
        }}>
        <div style={{ fontSize: '24px', marginBottom: '6px' }}>{file ? '📄' : '📁'}</div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '13px', marginBottom: '4px', wordBreak: 'break-all' }}>
          {file ? file.name : 'Clique para selecionar'}
        </div>
        <div style={{ fontSize: '11px', color: '#9898b8' }}>{file ? `${(file.size / 1024).toFixed(1)} KB` : dica}</div>
        <input id={id} type="file" accept=".xlsx,.xls,.csv" hidden
          onChange={e => { const f = e.target.files[0]; if (f) onPick(f); e.target.value = ''; }} />
      </div>
    </div>
  );
}

const card = { background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl = { fontSize: '11px', fontWeight: '700', color: '#52526e', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'monospace', display: 'block' };
const aviso = { borderRadius: '10px', border: '1px solid', padding: '12px 16px', marginBottom: '14px', fontSize: '13px' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: '11px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9898b8' };
const td = { padding: '8px 10px' };
const btn = (ativo) => ({
  background: ativo ? 'linear-gradient(135deg, #6c47ff, #5535e0)' : '#e5e7eb',
  color: ativo ? '#fff' : '#9898b8', border: 'none', borderRadius: '10px',
  padding: '11px 24px', fontSize: '14px', fontWeight: '600',
  cursor: ativo ? 'pointer' : 'not-allowed', fontFamily: 'DM Sans, sans-serif',
});
